import { FormattedMessage, useIntl } from 'react-intl';
import { Text } from '@understory-io/leaf/text';
import { Stack } from '@understory-io/leaf/stack';
import { LinkButton } from '@understory-io/leaf/link-button';
import { Button } from '@understory-io/leaf/button';
import { css } from '@understory-io/leaf/css';
import { env } from '~/platform/platform';
import {
  AvailabilityUnavailableNotice,
  type AvailabilityUnavailableNoticeProps,
} from '~/ui/availability-unavailable';

export function NotFoundPage() {
  // `<title>` requires a string child (React can't stringify a <FormattedMessage>
  // element), so resolve it via `intl.formatMessage` — same as UnexpectedErrorPage.
  const intl = useIntl();
  const title = intl.formatMessage({
    defaultMessage: 'Page not found',
    description: 'Browser tab title for the 404 error page',
  });
  return (
    <ErrorPageLayout>
      <title>{title}</title>
      <Text style="headline" as="h1">
        <FormattedMessage
          defaultMessage="Not found"
          description="Title shown on the 404 error page"
        />
      </Text>
      <Text>
        <FormattedMessage
          defaultMessage="The page wasn't found"
          description="Description shown on the 404 error page"
        />
      </Text>
      <LinkButton href="/" appearance="secondary">
        <FormattedMessage
          defaultMessage="Go back home"
          description="Button label to navigate home from the 404 error page"
        />
      </LinkButton>
    </ErrorPageLayout>
  );
}

export function UnexpectedErrorPage({ stack }: { stack?: string }) {
  const intl = useIntl();
  const title = intl.formatMessage({
    defaultMessage: 'Something went wrong',
    description: 'Browser tab title for the 500 error page',
  });
  return (
    <ErrorPageLayout>
      <title>{title}</title>
      <Text style="headline" as="h1">
        <FormattedMessage
          defaultMessage="Something went wrong"
          description="Title shown on the 500 error page"
        />
      </Text>
      <Text>
        <FormattedMessage
          defaultMessage="We don't know exactly what happened but the error has been logged and the team has been notified."
          description="Description shown on the 500 error page"
        />
      </Text>
      <Text style="label-medium">
        <FormattedMessage
          defaultMessage="What can you do?"
          description="Secondary heading on the 500 error page before action buttons"
        />
      </Text>
      <Stack direction="row" gap="small">
        <Button
          appearance="secondary"
          onClick={() => window.location.reload()}
          className={css({ flex: '1' })}
        >
          <FormattedMessage
            defaultMessage="Try again"
            description="Button label to reload the page on the 500 error page"
          />
        </Button>
        <LinkButton
          href="/"
          appearance="secondary"
          className={css({ flex: '1' })}
        >
          <FormattedMessage
            defaultMessage="Go back home"
            description="Button label to navigate home from the 500 error page"
          />
        </LinkButton>
      </Stack>
      {env.DEV && stack && (
        <pre
          className={css({
            maxWidth: "[100vw]",
            padding: 'small',
            background: 'surface.background',
            overflow: 'auto',
            textStyle: 'body-small-regular',
            borderRadius: 'small',
            textAlign: 'left',
          })}
        >
          {stack}
        </pre>
      )}
    </ErrorPageLayout>
  );
}

/**
 * Stands in for a booking step whose dates or times could not be checked: the
 * step has nothing else to show, and unlike an unexpected error, asking again
 * may well work.
 */
export function AvailabilityUnavailablePage({
  onRetry,
  retrying,
}: AvailabilityUnavailableNoticeProps) {
  const intl = useIntl();
  const title = intl.formatMessage({
    defaultMessage: 'Something went wrong',
    description: 'Browser tab title for the 500 error page',
  });
  return (
    <ErrorPageLayout>
      <title>{title}</title>
      <AvailabilityUnavailableNotice onRetry={onRetry} retrying={retrying} />
    </ErrorPageLayout>
  );
}

function ErrorPageLayout({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'start',
        justifyContent: 'center',
        flex: '1',
        paddingBlock: 'xlarge',
      })}
    >
      <Stack
        gap="medium"
        align="center"
        className={css({
          maxWidth: '[512px]',
          textAlign: 'center',
          px: 'medium',
        })}
      >
        {children}
      </Stack>
    </div>
  );
}
